import '@/plugins/vue-composition-api'
import '@/styles/styles.scss'
import '@/styles/admin.scss'
import { quillEditor, Quill } from 'vue-quill-editor'
import axios from 'axios'
import Vue from 'vue'
import { registerCardCropFormat } from '@/utils/campaignCardCrop'
import App from './App.vue'
import vuetify from './plugins/vuetify'
import router from './router'
import store from './store'
import { clearPanelSession, initializePanelAuth } from '@/utils/panelSession'

import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'

Vue.config.productionTip = false
registerCardCropFormat(Quill)
Vue.component('quill-editor', quillEditor)
axios.defaults.withCredentials = true

const panelRelease = process.env.VUE_APP_PANEL_RELEASE || 'development'
Vue.prototype.$panelRelease = panelRelease
document.documentElement.dataset.panelRelease = panelRelease

const escapeHtml = value => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;')

const renderFatalError = error => {
  const target = document.getElementById('app') || document.body

  if (!target) return

  const normalizeError = value => {
    if (typeof value === 'string') return value
    if (value instanceof Error) return value.stack || value.message
    if (value && typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2)
      } catch (jsonError) {
        return String(value)
      }
    }

    return String(value)
  }

  const detail = normalizeError(error)
  const showDetail = process.env.NODE_ENV !== 'production'

  target.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:32px;background:#0f172a;color:#e2e8f0;font-family:Inter,Roboto,sans-serif;">
      <div style="width:min(960px,100%);background:#111827;border:1px solid rgba(148,163,184,.24);border-radius:20px;padding:24px;box-shadow:0 24px 80px rgba(15,23,42,.45);">
        <div style="font-size:12px;letter-spacing:.12em;text-transform:uppercase;color:#94a3b8;margin-bottom:8px;">Panel Runtime Error</div>
        <h1 style="margin:0 0 12px;font-size:24px;line-height:1.2;color:#f8fafc;">Panel yüklenirken hata oluştu</h1>
        <p style="margin:0 0 ${showDetail ? '16px' : '0'};color:#cbd5e1;">Sayfayı yenileyip tekrar deneyin. Sorun devam ederse panel logları kontrol edilmelidir.</p>
        ${showDetail ? `<pre style="margin:0;white-space:pre-wrap;word-break:break-word;overflow:auto;background:#020617;border-radius:14px;padding:16px;color:#fda4af;">${escapeHtml(detail)}</pre>` : ''}
      </div>
    </div>
  `
}

Vue.config.errorHandler = (error, vm, info) => {
  // Keep console output for browser devtools and also show a visible fallback.
  console.error(error)
  renderFatalError(info ? `${info}\n\n${error?.stack || error?.message || error}` : error)
}

const storedTheme = localStorage.getItem('admin-theme-dark')
vuetify.framework.theme.dark = storedTheme === null ? true : storedTheme === 'true'
initializePanelAuth()

const publicRouteNames = new Set(['login', 'pages-register', 'error-404'])
let authRedirectInFlight = false

const redirectToLogin = () => {
  if (authRedirectInFlight) {
    return
  }

  authRedirectInFlight = true
  clearPanelSession()

  const currentRouteName = router.currentRoute?.name
  const releaseLock = () => {
    window.setTimeout(() => {
      authRedirectInFlight = false
    }, 0)
  }

  if (publicRouteNames.has(currentRouteName)) {
    releaseLock()

    return
  }

  router.replace({ name: 'login', query: { reset: '1' } })
    .catch(() => {})
    .finally(releaseLock)
}

axios.interceptors.response.use(
  response => response,
  error => {
    const status = error?.response?.status
    const requestUrl = String(error?.config?.url || '')
    const isAuthRequest = requestUrl.includes('/panel/login') || requestUrl.endsWith('/api/login')

    if ((status === 401 || status === 403) && !isAuthRequest) {
      redirectToLogin()
    }

    return Promise.reject(error)
  },
)

try {
  new Vue({
    router,
    store,
    vuetify,
    render: h => h(App),
  }).$mount('#app')
} catch (error) {
  renderFatalError(error)
}
